<script setup>
defineOptions({ name: "SolutionKaliStudioPage" });
import { defineAsyncComponent, onMounted, onUnmounted, ref } from "vue";
import {
  CalendarDaysIcon,
  ClipboardDocumentListIcon,
  BanknotesIcon,
  DocumentCheckIcon,
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  CreditCardIcon,
  PuzzlePieceIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  DeviceTabletIcon,
  ScaleIcon,
} from "@heroicons/vue/24/outline";
import {
  PaintBrushIcon,
  SparklesIcon,
  ScissorsIcon,
  FaceSmileIcon,
  HeartIcon,
  EyeIcon,
  SunIcon,
  CalendarDaysIcon as CalendarDaysIconSolid,
  UserGroupIcon,
} from "@heroicons/vue/24/solid";
import { useSeo } from "@/composables/useSeo";
import FaqPanel from "@/components/ui/FAQ.vue";
import SolutionHero from "@/components/ui/SolutionHero.vue";
import imageBg from "@/assets/solutions/kalistudio.webp";
import visaoGeralImg from "@/assets/visaogeralstudio.webp";
import gestaoTributaria from "@/assets/gestao-tributaria.webp";

const CTA = defineAsyncComponent(() => import("@/components/home/CTA.vue"));
const Clients = defineAsyncComponent(() => import("@/components/home/Clients.vue"));

// Revela o elemento com fade+slide assim que ele entra na viewport ao rolar a página.
const vReveal = {
  mounted(el) {
    el.classList.add("reveal-init");
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("reveal-visible");
          io.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" },
    );
    io.observe(el);
  },
};

const navLinks = [
  { label: "Visão geral", href: "#visao-geral" },
  { label: "Segmentos", href: "#segmentos" },
  { label: "Funcionalidades", href: "#funcionalidades" },
  { label: "Fiscal", href: "#fiscal" },
  { label: "Módulos", href: "#modulos" },
  { label: "Depoimentos", href: "#depoimentos" },
  { label: "Dúvidas", href: "#duvidas" },
];

const activeNav = ref(navLinks[0]?.href ?? "#visao-geral");
const setActiveNav = (href) => {
  activeNav.value = href;
};

const topOffsetPx = 140;
let scrollRaf = null;
const updateActiveFromScroll = () => {
  let current = navLinks[0]?.href ?? "#visao-geral";
  for (const link of navLinks) {
    const sectionEl = document.querySelector(link.href);
    if (!sectionEl) continue;
    const top = sectionEl.getBoundingClientRect().top;
    if (top - topOffsetPx <= 0) {
      current = link.href;
    } else {
      break;
    }
  }
  if (current !== activeNav.value) {
    setActiveNav(current);
  }
};

const onScroll = () => {
  if (scrollRaf) return;
  scrollRaf = requestAnimationFrame(() => {
    scrollRaf = null;
    updateActiveFromScroll();
  });
};

const handleNavClick = (event, href) => {
  event.preventDefault();
  setActiveNav(href);
  history.replaceState(null, "", href);
  const el = document.querySelector(href);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

const syncActiveNavFromHash = () => {
  const hash = window.location.hash;
  if (!hash) return;
  if (navLinks.some((l) => l.href === hash)) {
    setActiveNav(hash);
  }
};

onMounted(() => {
  syncActiveNavFromHash();
  window.addEventListener("hashchange", syncActiveNavFromHash, {
    passive: true,
  });
  window.addEventListener("scroll", onScroll, { passive: true });
  updateActiveFromScroll();
});

onUnmounted(() => {
  window.removeEventListener("hashchange", syncActiveNavFromHash);
  window.removeEventListener("scroll", onScroll);
  if (scrollRaf) cancelAnimationFrame(scrollRaf);
});

function openDemoForSegmento() {
  window.dispatchEvent(
    new CustomEvent("open-demo-modal", { detail: { segmento: "Salão de beleza / Estúdio" } }),
  );
}

// Conteúdo abaixo é baseado no ebook oficial "Kali Studio" (docs/books/KALI STUDIO - EBOOK.pdf).
const segmentos = [
  { icon: PaintBrushIcon, label: "Estúdios de tatuagem" },
  { icon: SparklesIcon, label: "Estúdios de piercing" },
  { icon: ScissorsIcon, label: "Barbearias" },
  { icon: FaceSmileIcon, label: "Salões de beleza" },
  { icon: HeartIcon, label: "Clínicas de estética" },
  { icon: SunIcon, label: "Depilação e harmonização" },
  { icon: EyeIcon, label: "Design de sobrancelhas" },
  { icon: CalendarDaysIconSolid, label: "Espaços com agenda e atendimento" },
  { icon: UserGroupIcon, label: "Estúdios com vários profissionais" },
];

const destaques = [
  {
    eyebrow: "Agendamento",
    title: "Agenda cheia, sem retrabalho",
    text: "Clientes visualizam a disponibilidade em tempo real e agendam online, escolhendo profissional ou horário, sem depender da recepção.",
    alt: "Agendamento online no Kali Studio",
  },
  {
    eyebrow: "Comandas",
    title: "Atendimento com comanda virtual",
    text: "Cada cliente tem uma comanda para adicionar serviços e produtos durante o atendimento, com fechamento integrado ao financeiro.",
    alt: "Comandas no Kali Studio",
  },
  {
    eyebrow: "Pagamento",
    title: "Cobrança já na hora do agendamento",
    text: "Com o Checkout Asaas, o cliente paga ao reservar o horário, reduzindo faltas e garantindo a agenda.",
    alt: "Pagamento integrado no Kali Studio",
  },
  {
    eyebrow: "Relacionamento",
    title: "Histórico e fidelização por WhatsApp",
    text: "Histórico completo de atendimentos e automação via WhatsApp para lembretes, campanhas e retorno de clientes.",
    alt: "Relacionamento com clientes no Kali Studio",
  },
];

const documentosFiscais = ["NF-e", "NFC-e", "NFS-e", "Carta de Correção"];

const controleTributario = [
  { label: "Sped Fiscal", shade: "bg-kali-blue-700" },
  { label: "Bloco H", shade: "bg-kali-blue-600" },
  { label: "Bloco K", shade: "bg-kali-blue-500" },
  { label: "EFD Contribuições", shade: "bg-kali-blue-400" },
];

const modulos = [
  {
    icon: CalendarDaysIcon,
    title: "Agenda",
    subtitle: "Controle total, sem faltas",
    items: [
      "Agenda por profissional e sala",
      "Link de agendamento com disponibilidade em tempo real",
      "Confirmação e lembretes automáticos",
      "Acesso por celular, tablet ou notebook",
    ],
  },
  {
    icon: ClipboardDocumentListIcon,
    title: "Comandas e atendimento",
    subtitle: "Do agendamento ao fechamento",
    items: [
      "Comanda virtual por cliente",
      "Serviços e produtos na mesma comanda",
      "Rateio de comissões",
      "Fechamento integrado ao financeiro",
    ],
  },
  {
    icon: BanknotesIcon,
    title: "Gestão financeira",
    subtitle: "Precisão no caixa",
    items: [
      "Contas a pagar e a receber",
      "Lançamentos recorrentes",
      "Planos de contas e centros de custo",
      "Abertura e fechamento de caixa",
    ],
  },
  {
    icon: DocumentCheckIcon,
    title: "Gestão fiscal",
    subtitle: "Simples e integrada",
    items: [
      "Emissão de NF-e e NFC-e",
      "Carta de correção",
      "Entrada de mercadorias via XML",
      "NFS-e pelo portal nacional",
    ],
  },
  {
    icon: ChatBubbleLeftRightIcon,
    title: "Relacionamento",
    subtitle: "Histórico e fidelização",
    items: [
      "Histórico completo de atendimentos",
      "Automação via WhatsApp",
      "Lembretes e campanhas",
      "Ações de retorno de clientes",
    ],
  },
  {
    icon: ChartBarIcon,
    title: "Gestão inteligente",
    subtitle: "Decisões com dados",
    items: [
      "Dashboards e relatórios",
      "Faturamento por profissional",
      "Serviços mais vendidos",
      "Resultados em poucos cliques",
    ],
  },
  {
    icon: CreditCardIcon,
    title: "Pagamento integrado",
    subtitle: "Checkout Asaas",
    items: [
      "Pagamento no momento do agendamento",
      "Reserva garantida",
      "Redução de faltas",
      "Direto pelo link de agendamento",
    ],
  },
  {
    icon: PuzzlePieceIcon,
    title: "Apps integrados",
    subtitle: "SEUPDV, SEUPOS, HUB e CONNECT",
    items: [
      "SEUPDV: PDV com venda offline e sincronização automática",
      "SEUPOS: app móvel com NFC-e, online e offline",
      "SEUPOS HUB: pagamentos centralizados com as adquirentes",
      "CONNECT: alternativa ao TEF tradicional",
    ],
  },
];

useSeo({
  title: "Sistema para Estúdios de Tatuagem e Piercing | Kali Studio",
  description:
    "Agendamentos online, controle de agenda, comandas, comissões, financeiro e fiscal no Kali Studio. Sistema 100% online para negócios que trabalham com atendimento.",
  image: imageBg,
  schema: {
    "@context": "https://schema.org",
    "@type": ["SoftwareApplication", "LocalBusiness"],
    name: "Kali Studio",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    url: "https://www.kalisistemas.com.br/solucao/kali-studio",
    description:
      "Sistema de gestão completo para negócios com agenda e atendimento, com agendamentos online, controle de profissionais, comandas, comissões, financeiro e emissão fiscal — tudo 100% online.",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "BRL",
    },
  },
});
</script>

<template>
  <div class="bg-white">
    <SolutionHero
      title="Kali Studio: agendamentos cheios, gestão simples e negócio em crescimento"
      description="O Kali Studio é um sistema completo para negócios que trabalham com agenda e atendimento. Agendamento online, operação ágil e controle total de agenda, comandas, finanças e fiscal em um só lugar."
      primary-label="Solicite uma demonstração gratuita"
      primary-href="/contato?type=kali-studio"
      :background-image="imageBg"
      background-class="bg-top bg-cover bg-slate-900"
      overlay-variant="left"
    >
    </SolutionHero>

    <section
      class="sticky top-16 z-40 border-b border-gray-100 shadow-sm backdrop-blur bg-white/95"
    >
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div class="flex gap-6 items-center h-16">
          <nav
            class="flex overflow-x-auto relative gap-6 items-center h-16"
            aria-label="Navegação da página"
          >
            <a
              v-for="item in navLinks"
              :key="item.href"
              :href="item.href"
              class="menu-item-underline relative inline-flex items-center px-2 h-16 text-sm font-bold text-gray-700 hover:text-[#006086] transition-all duration-300 whitespace-nowrap"
              :class="{ 'text-[#006086] active': activeNav === item.href }"
              @click="(e) => handleNavClick(e, item.href)"
            >
              {{ item.label }}
            </a>
          </nav>
        </div>
      </div>
    </section>

    <!-- Visão geral -->
    <section id="visao-geral" class="overflow-hidden relative py-16 bg-white scroll-mt-24 lg:min-h-[620px]">
      <div class="hidden absolute inset-y-0 right-0 w-1/2 lg:block">
        <img
          :src="visaoGeralImg"
          alt="Kali Studio"
          class="object-cover object-center w-full h-full"
        />
      </div>
      <div class="relative px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="lg:max-w-xl">
          <h2
            class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]"
          >
            Sobre o Kali Studio
          </h2>
          <p class="mt-6 text-lg leading-8 text-gray-600">
            O Kali Studio é um sistema completo desenvolvido para salões de
            beleza, barbearias, clínicas de estética, estúdios de tatuagem e
            piercing e qualquer negócio que trabalha com agenda e
            atendimento. Com ele, seus clientes agendam online, sua equipe
            ganha agilidade e você passa a ter controle total de agenda,
            comandas, finanças e fiscal em um só lugar — de qualquer
            dispositivo.
          </p>
          <div class="flex gap-8 items-center mt-10 text-[#006086]">
            <DevicePhoneMobileIcon class="w-10 h-10" />
            <ComputerDesktopIcon class="w-10 h-10" />
            <DeviceTabletIcon class="w-10 h-10" />
          </div>
        </div>
      </div>
    </section>

    <!-- Segmentos -->
    <section id="segmentos" class="py-16 bg-gray-100 scroll-mt-24">
      <div class="grid gap-12 items-center px-4 mx-auto max-w-7xl sm:px-6 lg:px-8 lg:grid-cols-[1.3fr_1fr]">
        <div class="grid grid-cols-2 gap-x-6 gap-y-10 sm:grid-cols-4">
          <div
            v-for="s in segmentos"
            :key="s.label"
            v-reveal
            class="flex flex-col items-center text-center group"
          >
            <component
              :is="s.icon"
              class="w-11 h-11 text-kali-blue-600 transition-transform duration-200 ease-out group-hover:-translate-y-1"
            />
            <p
              class="mt-3 text-xs font-semibold text-gray-800 transition-colors duration-200 sm:text-sm group-hover:text-kali-blue-600"
            >
              {{ s.label }}
            </p>
          </div>
        </div>
        <div v-reveal>
          <h2
            class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]"
          >
            Feito para negócios com
            <span class="text-kali-blue-600">agenda</span>
            e atendimento
          </h2>
          <p class="mt-4 text-base leading-7 text-gray-600">
            O Kali Studio se adapta a estúdios de tatuagem e piercing,
            salões de beleza, barbearias e clínicas de estética — qualquer
            negócio que vive de horário marcado e relacionamento com o
            cliente.
          </p>
          <button
            type="button"
            @click="openDemoForSegmento"
            class="inline-flex justify-center items-center px-6 py-3 mt-6 text-base font-semibold text-white rounded-full border border-transparent shadow-sm bg-kali-blue-500 hover:bg-kali-blue-600"
          >
            Atendemos negócios com agenda e atendimento
          </button>
        </div>
      </div>
    </section>

    <!-- Funcionalidades -->
    <section id="funcionalidades" class="py-20 bg-gradient-to-br from-white via-gray-50 to-[#e6f0f4] scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="max-w-2xl">
          <h2 class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-4">
            Recursos que fazem o Kali Studio funcionar na prática
          </h2>
          <p class="text-lg leading-8 text-gray-600">
            Quebre o caos da agenda, profissionalize o atendimento e ganhe
            controle de ponta a ponta.
          </p>
        </div>
        <div class="grid gap-8 mt-14 sm:grid-cols-2 lg:grid-cols-4">
          <div v-for="d in destaques" :key="d.title" v-reveal>
            <div
              class="flex justify-center items-center w-full h-44 rounded-2xl bg-gradient-to-br from-kali-blue-100 to-kali-blue-200"
            >
              <CalendarDaysIcon class="w-12 h-12 text-kali-blue-600" />
            </div>
            <p class="mt-4 text-xs font-bold tracking-wide text-[#006086] uppercase">
              {{ d.eyebrow }}:
            </p>
            <h3 class="mt-1 text-lg font-bold text-gray-900">{{ d.title }}</h3>
            <p class="mt-2 text-sm leading-relaxed text-gray-600">{{ d.text }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Reforma tributária -->
    <section class="px-4 py-16 mx-auto max-w-7xl sm:px-6 lg:px-8">
      <div
        v-reveal
        class="overflow-hidden relative bg-kali-blue-600 rounded-[3rem] shadow-xl"
      >
        <div class="grid gap-10 items-center px-8 py-12 lg:grid-cols-2 lg:px-16 lg:py-16">
          <div>
            <h2 class="text-2xl font-black tracking-tight text-white sm:text-3xl lg:text-4xl">
              Reforma tributária: é hora de se preparar!
            </h2>
            <p class="mt-4 text-base leading-7 text-kali-blue-100 sm:text-lg">
              O Kali Studio acompanha as mudanças da reforma tributária em
              tempo real, mantendo sua empresa em conformidade sem parar de
              vender.
            </p>
            <a
              href="/reforma-tributaria"
              class="inline-flex justify-center items-center px-8 py-3 mt-8 text-sm font-bold tracking-wide text-kali-blue-700 uppercase bg-white rounded-full shadow-sm transition hover:bg-kali-blue-50"
            >
              Saiba mais
            </a>
          </div>
          <div class="hidden relative lg:block lg:h-80">
            <img
              :src="gestaoTributaria"
              alt="Especialistas em conformidade fiscal"
              class="object-cover absolute inset-0 w-4/5 h-full rounded-3xl shadow-lg"
            />
            <div
              class="flex absolute -top-6 left-4 justify-center items-center w-16 h-16 rounded-2xl shadow-lg bg-kali-gold"
            >
              <DocumentCheckIcon class="w-8 h-8 text-white" />
            </div>
            <div
              class="flex absolute -bottom-6 right-4 justify-center items-center w-20 h-20 bg-cyan-400 rounded-2xl shadow-lg"
            >
              <ScaleIcon class="w-9 h-9 text-white" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Fiscal -->
    <section id="fiscal" class="py-16 bg-white scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div class="grid gap-12 lg:grid-cols-2">
          <div v-reveal>
            <h2 class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-3">
              Gestão fiscal
            </h2>
            <p class="mb-6 text-lg leading-8 text-gray-600">
              Emissão de NF-e, NFC-e, carta de correção e entrada de
              mercadorias via XML, além de NFS-e direto no portal nacional.
            </p>
            <div class="grid grid-cols-2 gap-3">
              <div
                v-for="d in documentosFiscais"
                :key="d"
                class="flex justify-center items-center py-4 text-base font-bold text-center rounded-lg bg-kali-blue-100 text-kali-blue-700"
              >
                {{ d }}
              </div>
            </div>
          </div>
          <div v-reveal>
            <h2 class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-3">
              Controle fiscal e tributário
            </h2>
            <p class="mb-6 text-lg leading-8 text-gray-600">
              Adequado para MEI, ME e Simples Nacional — os regimes mais
              comuns entre estúdios e salões, com emissão dos principais
              documentos fiscais.
            </p>
            <div class="grid grid-cols-2 gap-3">
              <div
                v-for="c in controleTributario"
                :key="c.label"
                class="flex items-end p-4 h-24 font-bold text-white rounded-lg"
                :class="c.shade"
              >
                {{ c.label }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Módulos -->
    <section id="modulos" class="py-16 bg-gradient-to-br from-white via-gray-50 to-[#e6f0f4] scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <h2
          class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-4"
        >
          Módulos do Kali Studio
        </h2>
        <p class="mt-4 mb-12 max-w-2xl text-lg leading-8 text-gray-600">
          Potencialize o Kali Studio com módulos que cobrem toda a rotina do
          seu negócio.
        </p>
        <div class="grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
          <div v-for="m in modulos" :key="m.title" v-reveal class="text-center">
            <component :is="m.icon" class="mx-auto w-10 h-10 text-[#006086]" />
            <h3 class="mt-4 text-lg font-bold text-gray-900">{{ m.title }}</h3>
            <p class="mt-1 text-sm font-semibold text-gray-700">{{ m.subtitle }}</p>
            <ul class="mt-4 space-y-2 text-sm text-gray-600">
              <li v-for="it in m.items" :key="it">{{ it }}</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Depoimentos -->
    <section id="depoimentos" class="scroll-mt-24">
      <Clients />
    </section>

    <section id="duvidas" class="scroll-mt-24">
      <FaqPanel
        :items="[
          {
            question: 'O Kali Studio serve para negócios com vários profissionais?',
            answer:
              'Sim. Você cadastra usuários, define permissões e acompanha resultados por profissional e por período.',
          },
          {
            question: 'Dá para confirmar e lembrar clientes automaticamente?',
            answer:
              'Sim. O sistema pode apoiar notificações e lembretes de agendamentos para reduzir faltas e desencontros.',
          },
          {
            question: 'Posso cobrar no momento do agendamento?',
            answer:
              'Sim. Com o Checkout Asaas, você pode oferecer pagamento na hora de reservar, garantindo o agendamento.',
          },
          {
            question: 'Emite nota fiscal?',
            answer: 'Sim. Emissão de NF-e, NFC-e e NFS-e, integrada ao financeiro.',
          },
        ]"
        description="Dúvidas sobre Kali Studio? Veja as respostas e fale com nosso time."
        cta-label="Falar com Consultor"
        :cta-opens-modal="true"
      />
    </section>

    <section id="fale-conosco" class="scroll-mt-16">
      <CTA />
    </section>
  </div>
</template>

<style scoped>
.reveal-init {
  opacity: 0;
  transform: translateY(28px);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;
}

.reveal-visible {
  opacity: 1;
  transform: translateY(0);
}

@media (prefers-reduced-motion: reduce) {
  .reveal-init {
    opacity: 1;
    transform: none;
    transition: none;
  }
}
</style>
